import type { GalleryMediaT } from '../../../../../type/galleryMediaT';

import {
  ProductGalleryImage,
  ProductGalleryPlaceholder,
  ProductGalleryVariant,
} from '@vs-shop/commerce-ui/productGallery';
import { ImageMissingPlaceholder } from '@vs-shop/ui/imageMissingPlaceholder';

import { resolveImageUrl } from '@vs-shop/engine/image/resolveImageUrl';

import { GalleryVideoMedia } from '../../../../client/page/product/gallery/GalleryVideoMedia';

import { getYoutubeVideoEmbedFromPath } from '../../../../../effect/utils/youtube/youtube';
import { ImageFilter } from '@vs-shop/engine/image/ImageFilter';

type Props = {
  media: GalleryMediaT;
  alt: string;
  isActive: boolean;
};

/**
 * One medium on the lightbox's stage: a photograph at this tenant's modal crop,
 * the stage's own empty box when the product has no abstract path to resolve, or
 * the video player.
 *
 * The photograph is handed to the image seam as a final URL and nothing else:
 * the CDN 404 → backend resolve → terminal placeholder chain is the seam's, and
 * a wrapper intercepting `onError` here would short-circuit it on the first
 * error and leave a first-visit image stuck on the placeholder.
 */
export function GalleryModalMainMedia({ media, alt, isActive }: Props) {
  if (media.mediaType === 'image') {
    const path = media.pathAbstract
      ? resolveImageUrl(media.pathAbstract, ImageFilter.product_gallery_modal_main)
      : '';

    if (!path) {
      return (
        <ProductGalleryPlaceholder variant={ProductGalleryVariant.OVERLAY}>
          <ImageMissingPlaceholder />
        </ProductGalleryPlaceholder>
      );
    }

    return (
      <ProductGalleryImage
        variant={ProductGalleryVariant.OVERLAY}
        src={path}
        alt={alt}
        width={1920}
        height={1080}
      />
    );
  }

  return (
    <GalleryVideoMedia
      src={getYoutubeVideoEmbedFromPath(media.path)}
      title={alt}
      isActive={isActive}
    />
  );
}
